// 定义格位结构
export interface GridPosition {
  row: number;   // 排
  column: number; // 列
  id: string;    // 格位ID（7位数字）
  isSold?: boolean; // 是否已销售
  price?: string; // 价格
  counts?: number; // 骨灰盅数量
}

// 定义存放室结构
export interface Room {
  id: number;    // 存放室编号 1-5
  positions: GridPosition[][]; // 二维数组表示格位
}

// 定义楼层结构
export interface Floor {
  id: number;    // 楼层编号 1-3
  rooms: Room[]; // 存放室数组
}

// 定义建筑结构
export interface Building {
  id: number;    // 建筑编号 1-3
  name: string;  // 建筑名称
  floors: Floor[]; // 楼层数组
}

// 定义格位详细信息
export interface PositionDetail {
  position: string; // 位置
  spirit_id: string; // 灵位编号
  price: string; // 价格
  sale_status: string; // 销售状态
  deceased_name: string | null; // 故者名称
  customer_name: string | null; // 客户名称
  purchase_date: string | null; // 购位日期
  payment_status: string | null; // 管理费欠费状态
  counts: number | null; // 骨灰盅数量
}

// API基础URL - 使用相对路径，自动适配当前域名和协议
const API_BASE_URL = '/api';

// 从API获取特定存放室的格位信息
export const fetchRoomPositions = async (buildingId: number, floorId: number, roomId: number): Promise<GridPosition[][]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/positions/${buildingId}/${floorId}/${roomId}`);
    if (!response.ok) {
      throw new Error('数据获取失败');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('获取格位数据出错:', error);
    return [];
  }
};

// 获取格位详细信息
export const fetchPositionDetail = async (positionId: string): Promise<PositionDetail | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/position/${positionId}`);
    if (!response.ok) {
      throw new Error('数据获取失败');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('获取格位详细信息出错:', error);
    return null;
  }
};

// 更新格位的骨灰盅数量
export const updatePositionCounts = async (positionId: string, counts: number): Promise<PositionDetail | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/position/${positionId}/counts`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ counts }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || '更新骨灰盅数量失败');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('更新骨灰盅数量出错:', error);
    return null;
  }
};

// 更新故者姓名
export const updateDeceasedName = async (positionId: string, deceasedName: string): Promise<PositionDetail | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/position/${positionId}/deceased_name`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ deceased_name: deceasedName }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || '更新故者姓名失败');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('更新故者姓名出错:', error);
    return null;
  }
};

// 为每个建筑创建格位数据
const generatePositionsForYiRanBuilding = (floorId: number, roomId: number): GridPosition[][] => {
  const positions: GridPosition[][] = [];
  
  // 第一层：第一排28个格位，2-11排各60个格位
  // 第二、三层：第一排28个格位，2-10排各60个格位
  const maxRow = floorId === 1 ? 11 : 10;
  
  // 为每一排生成格位
  for (let row = 1; row <= maxRow; row++) {
    const rowPositions: GridPosition[] = [];
    const maxColumn = row === 1 ? 28 : 60;
    
    for (let col = 1; col <= maxColumn; col++) {
      // 格位ID：建筑编号(1) + 楼层编号(1-3) + 存放室编号(1-5) + 排号(01-11) + 列号(01-60)
      const rowStr = row.toString().padStart(2, '0');
      const colStr = col.toString().padStart(2, '0');
      const id = `1${floorId}${roomId}${rowStr}${colStr}`;
      
      // 随机设置销售状态，低排位销售率更高
      const saleChance = Math.max(0, 0.7 - (row * 0.05)); // 第一排70%销售率，然后逐渐减少
      const isSold = Math.random() < saleChance;
      
      rowPositions.push({
        row,
        column: col,
        id,
        isSold
      });
    }
    
    positions.push(rowPositions);
  }
  
  return positions;
};

const generatePositionsForOtherBuildings = (buildingId: number, floorId: number, roomId: number): GridPosition[][] => {
  const positions: GridPosition[][] = [];
  
  // 第一层：第一排23个格位，2-11排各49个格位
  // 第二、三层：第一排23个格位，2-10排各49个格位
  const maxRow = floorId === 1 ? 11 : 10;
  
  // 为每一排生成格位
  for (let row = 1; row <= maxRow; row++) {
    const rowPositions: GridPosition[] = [];
    const maxColumn = row === 1 ? 23 : 49;
    
    for (let col = 1; col <= maxColumn; col++) {
      // 格位ID：建筑编号(2-3) + 楼层编号(1-3) + 存放室编号(1-5) + 排号(01-11) + 列号(01-49)
      const rowStr = row.toString().padStart(2, '0');
      const colStr = col.toString().padStart(2, '0');
      const id = `${buildingId}${floorId}${roomId}${rowStr}${colStr}`;
      
      // 随机设置销售状态，第二、三栋楼的销售率比第一栋楼低
      const baseSaleChance = buildingId === 2 ? 0.6 : 0.5; // 怡安楼60%基础销售率，怡乐楼50%基础销售率
      const saleChance = Math.max(0, baseSaleChance - (row * 0.05)); 
      const isSold = Math.random() < saleChance;
      
      rowPositions.push({
        row,
        column: col,
        id,
        isSold
      });
    }
    
    positions.push(rowPositions);
  }
  
  return positions;
};

// 为每个建筑创建房间
const generateRoomsForBuilding = (buildingId: number, floorId: number): Room[] => {
  const rooms: Room[] = [];
  
  for (let roomId = 1; roomId <= 5; roomId++) {
    let positions: GridPosition[][];
    
    if (buildingId === 1) { // 怡然楼
      positions = generatePositionsForYiRanBuilding(floorId, roomId);
    } else { // 怡安楼和怡乐楼
      positions = generatePositionsForOtherBuildings(buildingId, floorId, roomId);
    }
    
    rooms.push({
      id: roomId,
      positions
    });
  }
  
  return rooms;
};

// 为每个建筑创建楼层
const generateFloorsForBuilding = (buildingId: number): Floor[] => {
  const floors: Floor[] = [];
  
  for (let floorId = 1; floorId <= 3; floorId++) {
    floors.push({
      id: floorId,
      rooms: Array.from({ length: 5 }, (_, index) => ({
        id: index + 1,
        positions: [] // 初始化为空数组，将通过API获取实际数据
      }))
    });
  }
  
  return floors;
};

// 创建所有建筑数据
export const buildings: Building[] = [
  {
    id: 1,
    name: '怡然楼',
    floors: generateFloorsForBuilding(1)
  },
  {
    id: 2,
    name: '怡安楼',
    floors: generateFloorsForBuilding(2)
  },
  {
    id: 3,
    name: '怡乐楼',
    floors: generateFloorsForBuilding(3)
  }
];
